import { useCallback, useEffect, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { FormInput, FormLabel, FormRow } from '@ui/components/Form/Form';
import { Tabs } from '@ui/components/Tabs';

import { components } from '@features/shared/api/club/clients';
import { TournamentEditFormValues } from '@features/tournament/ui/components/TournamentSettings/typings';

import {
  GRAND_PRIX_AGE_OPTIONS,
  GRAND_PRIX_AGE_TICKET_PREFIX,
  SCHOOL_ELO_OPTIONS,
  SCHOOL_ELO_TICKET_PREFIX,
  GRAND_PRIX_TICKET,
  PER_EVENT_TICKET_WITH_COUNTER,
  PREMIUM_RCF_ELO_OPTIONS,
  PREMIUM_RCF_ELO_TICKET_PREFIX,
  PREMIUM_RCF_TICKET,
  SCHOOL_TICKET,
  GRAND_PRIX_PROMO_PAGE,
  SUBSCRIPTION_TICKET,
  PREMIUM_RCF_PROMO_PAGE,
  PREMIUM_ACCOUNT_PAGE,
} from '../../constants';

type ITicketsSet = string[][] | null | undefined;
type IUpdate = {
  rcfRankCalculated: boolean;
  entranceTicketSets: ITicketsSet;
  paymentSettings: components['schemas']['TournamentPaymentSettings'] | null;
};

type IModel = { migration?: string[][] | null } & (
  | { type: 'shared-premium' }
  | { type: 'premium-rcf'; elo: string }
  | { type: 'grand-prix'; age: string }
  | { type: 'school'; elo: string }
  | { type: 'regular' | 'rcf' }
);
type ISchoolSwitch = IModel['type'];
type IPremiumType = 'shared-premium' | 'premium-rcf' | 'grand-prix';

const OPTIONS: {
  value: ISchoolSwitch;
  label: string;
  disabled?: boolean;
}[] = [
  { value: 'regular', label: 'Обычный' },
  { value: 'school', label: 'Ученики РШШ' },
  { value: 'shared-premium', label: 'Премиум аккаунт' },
  { value: 'rcf', label: 'Бесплатный ФШР' },
];

const OPTIONS_PREMIUM: {
  value: IPremiumType;
  label: string;
  disabled?: boolean;
}[] = [
  { value: 'shared-premium', label: 'Все' },
  { value: 'premium-rcf', label: 'ФШР по подписке' },
  { value: 'grand-prix', label: 'Гран-при РШШ' },
];

// Актуальные промо-/платёжные ссылки для каждого премиум-типа
const PREMIUM_LINK_BY_TYPE: Record<IPremiumType, string> = {
  'shared-premium': PREMIUM_ACCOUNT_PAGE,
  'premium-rcf': PREMIUM_RCF_PROMO_PAGE,
  'grand-prix': GRAND_PRIX_PROMO_PAGE,
};

export function ChessRussianClubTickets() {
  const { control, setValue } = useFormContext<TournamentEditFormValues>();

  const [rcfRankCalculated, entranceTicketSets, system, paymentSettings] = useWatch({
    name: ['rcfRankCalculated', 'entranceTicketSets', 'system', 'paymentSettings'],
    control,
  });

  const isSeance = system === 'Seance';

  // Текущие выбранные значения:
  const selected = useMemo((): IModel => {
    let isSubscription = false;
    let isPerEvent = false;

    let isPremiumRcf = false;
    let isGrandPrix = false;
    let isSchool = false;

    let eloSchool;
    let eloPremiumRcf;
    let grandPrixAge;

    const flatTickets = entranceTicketSets?.flatMap((t) => t) ?? [];

    for (const ticket of flatTickets) {
      if (ticket === SUBSCRIPTION_TICKET) isSubscription = true;
      else if (ticket === PER_EVENT_TICKET_WITH_COUNTER) isPerEvent = true;
      else if (ticket === GRAND_PRIX_TICKET) isGrandPrix = true;
      else if (ticket === PREMIUM_RCF_TICKET) isPremiumRcf = true;
      else if (ticket === SCHOOL_TICKET) isSchool = true;
      else if (ticket.startsWith(PREMIUM_RCF_ELO_TICKET_PREFIX)) eloPremiumRcf = ticket;
      else if (ticket.startsWith(GRAND_PRIX_AGE_TICKET_PREFIX)) grandPrixAge = ticket;
      else if (ticket.startsWith(SCHOOL_ELO_TICKET_PREFIX)) eloSchool = ticket;
    }

    if (isPremiumRcf && isGrandPrix) {
      const migration = buildSharedPremiumTickets();
      return {
        type: 'shared-premium',
        migration:
          !isSubscription ||
          !isPerEvent ||
          !eloPremiumRcf ||
          !grandPrixAge ||
          flatTickets.length !== migration.flatMap((t) => t).length
            ? migration
            : null,
      };
    }

    if (isPremiumRcf || eloPremiumRcf) {
      const elo = eloPremiumRcf ?? PREMIUM_RCF_ELO_OPTIONS[0]!.value;
      const migration = buildPremiumRcfTickets(elo);
      return {
        type: 'premium-rcf',
        elo,
        migration:
          !isPremiumRcf ||
          !eloPremiumRcf ||
          !isSubscription ||
          !isPerEvent ||
          flatTickets.length !== migration.flatMap((t) => t).length
            ? migration
            : null,
      };
    }

    // sic! абонементы раньше были только для гран-при
    if (isGrandPrix || grandPrixAge || isSubscription || isPerEvent) {
      const age = grandPrixAge ?? GRAND_PRIX_AGE_OPTIONS[0]!.value;
      const migration = buildGrandPrixTickets(age);
      return {
        type: 'grand-prix',
        age,
        migration:
          !isGrandPrix ||
          !grandPrixAge ||
          !isSubscription ||
          !isPerEvent ||
          flatTickets.length !== migration.flatMap((t) => t).length
            ? migration
            : null,
      };
    }

    if (isSchool || eloSchool) {
      const elo = eloSchool ?? SCHOOL_ELO_OPTIONS[0]!.value;
      const migration = buildSchoolTickets(elo);
      return {
        type: 'school',
        elo,
        migration:
          !isSchool || !eloSchool || flatTickets.length !== migration.flatMap((t) => t).length
            ? migration
            : null,
      };
    }

    return { type: rcfRankCalculated ? 'rcf' : 'regular' };
  }, [rcfRankCalculated, entranceTicketSets]);

  // Нормализация/миграция существующих тикетов
  useEffect(() => {
    if (selected.migration) {
      setValue('entranceTicketSets', selected.migration, { shouldDirty: true });
    }
  }, [selected.migration, setValue]);

  // Нормализация/миграция существующих paymentSettings
  const paymentMigration = useMemo(() => {
    if (!isPremiumType(selected.type) || !paymentSettings) return null;

    const next = buildPremiumPaymentSettings(selected.type, isSeance);

    if (
      paymentSettings.promoLink === next.promoLink &&
      paymentSettings.paymentLink === next.paymentLink &&
      paymentSettings.promoTitle === next.promoTitle
    ) {
      return null;
    }

    return next;
  }, [selected.type, paymentSettings, isSeance]);

  useEffect(() => {
    if (paymentMigration) {
      setValue('paymentSettings', paymentMigration, { shouldDirty: true });
    }
  }, [paymentMigration, setValue]);

  const handleUpdate = useCallback(
    (data: IUpdate) => {
      setValue('rcfRankCalculated', data.rcfRankCalculated);
      setValue('entranceTicketSets', data.entranceTicketSets);
      setValue('paymentSettings', data.paymentSettings);
    },
    [setValue],
  );

  const mainType =
    selected.type === 'shared-premium' ||
    selected.type === 'grand-prix' ||
    selected.type === 'premium-rcf'
      ? 'shared-premium'
      : selected.type;

  return (
    <>
      <FormRow>
        <FormLabel>Тип турнира в РШШ</FormLabel>
        <FormInput>
          <Controller
            name="entranceTicketSets"
            control={control}
            render={({ field: { disabled } }) => (
              <TypeSwitch
                value={mainType}
                disabled={disabled}
                onUpdate={handleUpdate}
                isSeance={isSeance}
              />
            )}
          />
        </FormInput>
      </FormRow>
      {mainType === 'shared-premium' && (
        <FormRow>
          <FormLabel>Проект</FormLabel>
          <FormInput>
            <Controller
              name="entranceTicketSets"
              control={control}
              render={({ field: { disabled } }) => (
                <PremiumSwitch
                  value={selected.type}
                  disabled={disabled}
                  onUpdate={handleUpdate}
                  isSeance={isSeance}
                />
              )}
            />
          </FormInput>
        </FormRow>
      )}
      {selected.type === 'school' && (
        <FormRow>
          <FormLabel>Рейтинговая группа</FormLabel>
          <FormInput>
            <Controller
              name="entranceTicketSets"
              control={control}
              render={({ field: { onChange, disabled } }) => (
                <Tabs
                  tabs={SCHOOL_ELO_OPTIONS}
                  value={selected.elo}
                  onChange={(v) => onChange(buildSchoolTickets(v))}
                  disabled={disabled}
                />
              )}
            />
          </FormInput>
        </FormRow>
      )}
      {selected.type === 'grand-prix' && (
        <FormRow>
          <FormLabel>Возрастная группа</FormLabel>
          <FormInput>
            <Controller
              name="entranceTicketSets"
              control={control}
              render={({ field: { onChange, disabled } }) => (
                <Tabs
                  tabs={GRAND_PRIX_AGE_OPTIONS}
                  value={selected.age}
                  onChange={(v) => onChange(buildGrandPrixTickets(v))}
                  disabled={disabled}
                />
              )}
            />
          </FormInput>
        </FormRow>
      )}
      {selected.type === 'premium-rcf' && (
        <FormRow>
          <FormLabel>Рейтинговая группа</FormLabel>
          <FormInput>
            <Controller
              name="entranceTicketSets"
              control={control}
              render={({ field: { onChange, disabled } }) => (
                <Tabs
                  tabs={PREMIUM_RCF_ELO_OPTIONS}
                  value={selected.elo}
                  onChange={(v) => onChange(buildPremiumRcfTickets(v))}
                  disabled={disabled}
                />
              )}
            />
          </FormInput>
        </FormRow>
      )}
    </>
  );
}

function TypeSwitch({
  value,
  disabled,
  isSeance,
  onUpdate,
}: {
  value: ISchoolSwitch;
  disabled?: boolean;
  isSeance: boolean;
  onUpdate: (data: IUpdate) => void;
}) {
  const onChange = useCallback(
    (v: ISchoolSwitch): void => {
      const result = ((): IUpdate => {
        switch (v) {
          case 'shared-premium':
            return {
              entranceTicketSets: buildSharedPremiumTickets(),
              rcfRankCalculated: true,
              paymentSettings: buildPremiumPaymentSettings(v, isSeance),
            };
          case 'school':
            return {
              entranceTicketSets: buildSchoolTickets(),
              rcfRankCalculated: false,
              paymentSettings: null,
            };
          case 'rcf':
            return {
              entranceTicketSets: null,
              rcfRankCalculated: true,
              paymentSettings: null,
            };
          default:
            return {
              entranceTicketSets: null,
              rcfRankCalculated: false,
              paymentSettings: null,
            };
        }
      })();

      onUpdate(result);
    },
    [isSeance, onUpdate],
  );

  return <Tabs tabs={OPTIONS} value={value} onChange={onChange} disabled={disabled} />;
}

function PremiumSwitch({
  value,
  disabled,
  isSeance,
  onUpdate,
}: {
  value: ISchoolSwitch;
  disabled?: boolean;
  isSeance: boolean;
  onUpdate: (data: IUpdate) => void;
}) {
  const onChange = useCallback(
    (v: ISchoolSwitch): void => {
      const result = ((): IUpdate => {
        switch (v) {
          case 'grand-prix':
            return {
              entranceTicketSets: buildGrandPrixTickets(),
              rcfRankCalculated: true,
              paymentSettings: buildPremiumPaymentSettings(v, isSeance),
            };
          case 'premium-rcf':
            return {
              entranceTicketSets: buildPremiumRcfTickets(),
              rcfRankCalculated: true,
              paymentSettings: buildPremiumPaymentSettings(v, isSeance),
            };
          case 'shared-premium':
            return {
              entranceTicketSets: buildSharedPremiumTickets(),
              rcfRankCalculated: true,
              paymentSettings: buildPremiumPaymentSettings(v, isSeance),
            };
          default:
            return {
              entranceTicketSets: null,
              rcfRankCalculated: false,
              paymentSettings: null,
            };
        }
      })();

      onUpdate(result);
    },
    [isSeance, onUpdate],
  );

  return <Tabs tabs={OPTIONS_PREMIUM} value={value} onChange={onChange} disabled={disabled} />;
}

function isPremiumType(type: IModel['type']): type is IPremiumType {
  return type in PREMIUM_LINK_BY_TYPE;
}

function buildPremiumRcfTickets(elo?: (typeof PREMIUM_RCF_ELO_OPTIONS)[number]['value']) {
  return [
    [
      SUBSCRIPTION_TICKET,
      PER_EVENT_TICKET_WITH_COUNTER,
      PREMIUM_RCF_TICKET,
      elo ?? PREMIUM_RCF_ELO_OPTIONS[0]!.value,
    ],
  ];
}

function buildGrandPrixTickets(age?: (typeof GRAND_PRIX_AGE_OPTIONS)[number]['value']) {
  return [
    [
      SUBSCRIPTION_TICKET,
      PER_EVENT_TICKET_WITH_COUNTER,
      GRAND_PRIX_TICKET,
      age ?? GRAND_PRIX_AGE_OPTIONS[0]!.value,
    ],
  ];
}

function buildSchoolTickets(elo?: (typeof SCHOOL_ELO_OPTIONS)[number]['value']) {
  return [[SCHOOL_TICKET, elo ?? SCHOOL_ELO_OPTIONS[0]!.value]];
}

function buildSharedPremiumTickets() {
  return [
    [
      SUBSCRIPTION_TICKET,
      PER_EVENT_TICKET_WITH_COUNTER,
      // гран-при
      GRAND_PRIX_TICKET,
      GRAND_PRIX_AGE_OPTIONS[0]!.value,
      // фшр
      PREMIUM_RCF_TICKET,
      PREMIUM_RCF_ELO_OPTIONS[0]!.value,
    ],
  ];
}

function buildPremiumPaymentSettings(
  type: IPremiumType,
  isSeance: boolean,
): components['schemas']['TournamentPaymentSettings'] {
  const link = PREMIUM_LINK_BY_TYPE[type];

  return {
    promoTitle: formatPromoTitle(isSeance),
    promoLink: link,
    paymentLink: link,
  };
}

function formatPromoTitle(isSeance: boolean) {
  return isSeance ? 'Событие доступно по подписке' : 'Официальный турнир на рейтинг ФШР';
}
